/**
 * Form-View is used for form validation and toggling the primary and cancel
 * buttons as appropriate.  NOTE YOUR FORM MUST BE CONTAINED IN A FORM TAG.
 *
 * Usage:
 *
 * tagName: 'form', // IMPORTANT!
 *
 * // create the formView in the render method instead of the initialize method
 * // to ensure if the view is re-rendered or re-created, the formView has a
 * // reference to the correct this.el
 * render: function () {
 *     this.formView = new FormView({
 *         el: this.el,
 *         // Have the FormView listen for submit instead of adding 'submit' to
 *         // this.events
 *         submit: this.submit.bind(this),
 *         <other opts - see initialize() jsdoc below>
 *     })
 *
 *     // in case you want to know when something changed on the form.  Note it
 *     // may be more appropriate to extend FormView and overwrite the test and
 *     // validate methods, depending on what you're doing
 *     this.formView.on('change', this.change.bind(this))
 *
 *     // all your rendering logic goes here
 *
 *     // IMPORTANT: must call start!
 *
 *     // at the end of the method, start watching for changes from the user
 *     // passing in this.model.isNew() is only necessary on edit tabs
 *     this.formView.start({ isNew: this.model.isNew() })
 * },
 *
 * Other options:
 * // Disable the submit button
 * this.formView.primaryButton.disable()
 *
 * // Show an error
 * this.formView.errorMessage(i18n.get(''))
 * // Clears the messages displayed in the .banner-error
 * this.formView.clearErrors()
 *
 * // To provide additional validation, provide a test function in the options:
 * this.formView = new FormView({
 * 	el: this.el,
 * 	test: () => {
 * 		if (this.somethingBad) {
 * 			return i18n.get('some.error.message')
 * 		}
 * 	}
 * })
 *
 * // If you *really* need to override the test/validation functionality, you
 * // can use FormView.extend(). However, **this is no longer the preferred
 * // method for adding extra validation**. Instead, passing a "test" function
 * // to the constructor is preferred.
 * var MyForm = FormView.extend({
 *     test: this.test.bind(this),
 *     validate: this.validate.bind(this)
 * })
 */

import $ from 'jquery'

import Backbone from 'backbone'
import BusyButton from 'scoreboard/components/busy-button'
import _ from 'lodash'
import formHelper from 'scoreboard/components/form-helper'
import ErrorView from 'scoreboard/components/error-view'
import appDisplay from 'scoreboard/components/app-display'
import KeyCodes from 'scoreboard/components/key-codes'
import dialogStack from 'scoreboard/components/dialog-stack'
import guard from 'scoreboard/components/unsaved/guard'
import i18n from 'scoreboard/components/i18n'
import HttpStatusCodes from 'http-status-codes'
import {isOffline} from 'scoreboard/components/xhr'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import displayErrorHandler from 'scoreboard/components/display-error-handler'
import conductor from 'scoreboard/components/conductor'

// TODO: consider removing cancelOnToggle option, since we already have to
// inspect the dialog for issue #6226.  We could just look if the view is
// inside a dialog and base cancelOnToggle on that instead

/**
 * Used for form validation and toggling the primary and cancel
 * buttons as appropriate.
 *
 * Typically a FormView is provided an el that is a <form> but if this is not possible
 * (e.g. due to nested forms; record-list-widget.js) then the calling view is
 * responsible for setting up it's own event that triggers submit()
 *
 * @class FormView
 */
var FormView = Backbone.View.extend({

	events: {
		'keyup': 'cancelOnEsc',
		// #11609 #11634 checkboxes don't reliably respond to the input event
		'change input[type=checkbox], input[type=radio], select, textarea': 'change',
		// #10563 file inputs are handled in file-input.js and don't want formView involved
		'input input:not([type=file]), textarea': 'change',
		'input [contenteditable]': 'change',
		'click .banner-error': 'onClickBannerError',
		'click .cancel': 'cancel',
		'click .button-secondary': 'submitSecondary',
		'submit': 'submit'
	},

	cancelOnEsc: function(e) {
		// if there isn't a dialog open, cancel the form. (Dialogs automatically
		// close on their own if you press escape, and if a dialog is displayed
		// from an edit tab, we don't want to cancel the edit tab at the
		// same time as the dialog)
		if (e.keyCode == KeyCodes.ESC
			&& _.result(this.options, 'cancelOnEsc', true)
			&& !dialogStack.peek()) {

			this.cancelButtonEl.click()
			this.guard && guard.removeGuard(this.guard)
		}
	},

	/**
	 * @typedef FormViewOptions
	 *
	 * @property {Object} options
	 *
	 * @property {jQuery|Element} options.el the element containing this form
	 *
	 * @property {String} [options.id] A form id (name), if provided any buttons (and
	 * their corresponding listeners) will only be effective if they have a
	 * 'form' attribute that matches this value. (e.g. record-list-widget.hbs)
	 *
	 * @property {Function} [options.submit] A function to invoke when the
	 * primary button is triggered and the form is valid.
	 *
	 * @property {Function} [options.submitSecondary] A function to invoke when the
	 * secondary button is triggered and the form is valid.
	 *
	 * @property {Function} [options.test] A function to provide additional
	 * form validation beyond the default behavior in FormView.test().
	 * Should return a String or array of Strings with error messages, or null
	 * if there are no errors.
	 *
	 * @property {boolean} [options.guard=true] Forms will have an unsaved guard,
	 * if this is not needed for the view, set to false
	 *
	 * @property {Function} [options.guardFn] Allows the caller to provide their own
	 * guard if a non-standard one is required. (e.g. forms)
	 *
	 * @property {Function|boolean} [options.allowCleanSubmission=false]
	 * when you want to allow clicking next
	 * even if there are no changes in the form (e.g. maybe you're creating
	 * something new and all the defaults are OK, or you're in a wizard where
	 * all the fields are optional).
	 * Can be truthy or a function that returns a truthy value
	 *
	 * @property {GuardOptions} [options.guardOptions]
	 *
	 * @property {Function|boolean} [options.cancelOnEsc=true] If true, cancels the
	 * form when ESC is pressed. Can be truthy or a function that returns a truthy value
	 *
	 * @property {jQuery|Element} [options.cancelButtonEl=this.$('.cancel')] allows
	 * explicitly setting the cancel button in case there's some ambiguity
	 *
	 * @property {jQuery|Element} [options.primaryButtonEl=this.$('.button-primary').last()]
	 * allows explicitly setting the primary button element in case there's some
	 * ambiguity
	 *
	 * @property {jQuery|Element} [options.secondaryButtonEl=this.$('.button-secondary').last()]
	 * allows explicitly setting the cancel button in case there's some ambiguity
	 *
 	 * @property {boolean} [options.preventButtonSetup=false] If true, no
	 * primary or secondary buttons will be set up. (e.g. forms view tab where
	 * there are no buttons to submit the form)
	 *
	 */

	/**
	 * @constructs
	 * @param {FormViewOptions} options
	 */
	initialize: function(options = {}) {
		this.options = options
		this.id = options.id
		// outside dialogs, we need the cancel button to be toggled based on
		// whether there is anything to toggle, so pass in true outside dialogs
		// this will default to false, so you don't need to pass this in for dialogs
		this.toggleCancel = _.isUndefined(this.options.toggleCancel) ? false : true
	},

	/**
	 * @typedef FormViewStartOptions
	 *
	 * @property {Boolean} [isNew=false] for new forms, hitting cancel should send
	 * the user back to the edit form for the parent (or relative)
	 *
	 * @property {Boolean} [instadone=false] true if the primary submit button should immediately
	 * run the "done" animation
	 *
	 * @property {Boolean} [skipDoneAnimation=false] The default behavior for BusyButton's
	 * that are loading is to show the done animation and then disable it.
	 * Pass true for this option if it should go straight to disabled state.
	 */

	/**
	 * Start listening for changes to the form.  This should also be used to reset
	 * a form (and the button state) after it has been submitted.
	 *
	 * @param {FormViewStartOptions} options
	 */
	start: function(options = {}) {
		const { isNew} = options
		// If form has previously been submitted, remove the faded out appearance and enable buttons
		this._setSubmitting(false)

		// #55529 in case FormView.start is called multiple times, if isNew is
		// undefined, don't overwrite a valid value for this.isNew
		this.isNew = isNew ?? this.isNew
		// #16659 this.change marks things dirty wrapped in process.nextTick, so
		// this has to be wrapped as well
		process.nextTick(() => this.dirty = false)
		this.started = true
		this.setupButtons(options)
		// if we're on an edit tab
		if (this.toggleCancel) {
			if (!_.isUndefined(isNew) && isNew) {
				// for new forms, hitting cancel should send us back to the
				// edit form for the parent (or relative), so it should always
				// be enabled
				this.cancelButtonEl.prop('disabled', false)
			} else {
				this.cancelButtonEl.prop('disabled', true)
			}
		}

		// an example of when this is needed is on app admin when not enough
		// info is specified for export replication so the replicationDatasinks
		// field becomes marked invalid.  after correcting the error and
		// submitting the form we need to clear the error
		_.invokeMap(formHelper.getMarkedFields(this.el), 'clearError')

		this.clearErrors()

		// if clean submissions are allowed, enable the submit button if
		// there are no errors
		if (this.allowCleanSubmission()) {
			let enable = !this.test()
			_.each(this.submitButtons, b => b.toggle(enable))
		}

		// needed if the elements changed after the formView was first created
		// e.g. on the admin | users screen
		this.setElement(this.el)

		this.guard && guard.removeGuard(this.guard)
		const {guardFn, guard: applyGuard} = this.options
		if (guardFn) {
			this.guard = guardFn()
		} else if (applyGuard !== false) {
			this.guard = guard.standardGuard(this, this.options.guardOptions)
		}

		return this
	},

	/**
	 * @param {FormViewStartOptions} options
	 */
	setupButtons: function (options = {}) {
		if (this.options.preventButtonSetup) {
			return
		}
		const primaryButtonEl = options.primaryButtonEl ??
			this._getFormButton('primaryButtonEl', '.button-primary')
		const secondaryButtonEl = this._getFormButton('secondaryButtonEl', '.button-secondary')
		this.cancelButtonEl = options.cancelButtonEl ??
			this._getFormButton('cancelButtonEl', '.cancel')

		// always create a new busy-button in case we the previous one was removed
		this.primaryButton = new BusyButton({el: primaryButtonEl})
		if (!_.isEmpty(secondaryButtonEl)) {
			this.secondaryButton = new BusyButton({el: secondaryButtonEl})
		}

		//if there are new button, remove the old ones
		let existingPrimaryButtonEl = _.get(this.submitButtons,'[0].el')
		if (existingPrimaryButtonEl && existingPrimaryButtonEl !== primaryButtonEl[0]) {
			// remove() as a method on HTMLElements is not supported by IE11 so
			// use the Jquery remove method instead. Issue #31764
			$(existingPrimaryButtonEl).remove()
			_.invokeMap(this.submitButtons, 'remove')
			delete this.submitButtons
		} else if (!_.isEmpty(this.submitButtons)) {
			// #28901 otherwise retain the existing ones
			this.primaryButton = _.get(this.submitButtons,'[0]')
			this.secondaryButton = _.get(this.submitButtons,'[1]')
		}

		let allowCleanSubmission = this.allowCleanSubmission()

		const {instadone} = options
		if (!this.submitButtons) {
			this.submitButtons = []
			this.submitButtons.push(this.primaryButton)
			this.secondaryButton && this.submitButtons.push(this.secondaryButton)
			_.each(this.submitButtons, b => b.disable())
			// https://github.com/SpiderStrategies/Scoreboard/issues/21509#issuecomment-457237838
			// Because this.change modifies the button state in a next tick
			// we also have to do this here for the same reason
			process.nextTick(() => {
				this._updateButtons(false)
				instadone && this.primaryButton.instadone(allowCleanSubmission)
			})
		} else {
			_.each(this.submitButtons, btn => {
				if (btn.state === 'loading') {
					options.skipDoneAnimation ? btn.disable() : btn.done()
				} else {
					// the submit button(s) should always start disabled, but NEVER
					// have disabled="true".  In the case where nothing was changed
					// and the form is also invalid, we prevent the click from
					// doing anything in the submit method.
					btn.disable()
				}
			})
			instadone && this.primaryButton.instadone(allowCleanSubmission)
		}
	},

	/**
	 * Returns the last button (if there are multiple) of a given type inside the
	 * form; optionally filtering out buttons that are members of other (logical)
	 * nested forms.  HTML doesn't allow nested forms, but in some cases
	 * (e.g. record-list-widget.js)
	 * we have Backbone views that self encapsulate a logical form, and we give
	 * their form elements names following the HTML5 input.form property rules so
	 * that they are not picked up by the outer form.
	 *
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefform
	 *
	 * @param {JQuery|Element} option The name of a this.options property that can be used to
	 * target a given form button (e.g. 'primaryButtonEl')
	 *
	 * @param {String} selector the default css selector to locate the button
	 *
	 * @returns {JQuery<HTMLElement>}
	 */
	_getFormButton: function(option, selector) {
		return _.result(this.options, option, () => {
			// Filter out any buttons that are in nested forms (e.g. record-widget-list)
			return this.$(selector).filter((i,el) => {
				const formName = $(el).attr('form')
				return _.isNil(formName) || formName == this.id
			}).last()
		})
	},

	/**
	 * A method for retrieving the primary button which always queries the DOM
	 * so if the primary button was replaced, we are referencing the correct
	 * one.
	 */
	getPrimaryButton: function() {
		var el = this._getFormButton(undefined, '.button-primary')
		if (el) {
			var existing = el.last().data('view')
			if (existing) {
				return existing
			} else {
				return new BusyButton({ el: el.last() })
			}
		}
		return null
	},

	// handles when something changes on the form by toggling the cancel
	// and primary buttons as appropriate
	change: function(e) {
		if (!this.started) {
			return
		}
		this._terminateEvent(e)
		// ignore fields that should be ignored
		if (e && e.target) {
			if ($(e.target).hasClass('is-ignoring-change') ||
				$(e.target).closest('.is-ignoring-change').length > 0) {
				return
			}
		}
		//For IE11, each input with non-ascii generates false "input"
		//events when initially render, so ignore each one of these events
		if (this._ignoreInputEventCounter > 0) {
			this._ignoreInputEventCounter -= 1
			return
		}

		// useful for tests so they don't have to listen to wait for
		// process.nextTick
		this.trigger('before-change', e)

		process.nextTick(() => {
			// if the form contents change based on the values for other controls on
			// the form, we need to give those changes a chance to happen because
			// formView will always end up registering its event listeners first
			this.dirty = true
			this._updateButtons()

			// We've just made some kind of change to the form, that means form elements
			// might have been shifted around a bit. Let's make sure any tooltips
			// associated with marked fields in the form update their positioning (#29465).
			_.each(this.$('.marked-field'), field => {
				_.invoke(this.$(field).data(), 'view.tooltip.reposition')
			})

			this.trigger('change', e)
		})
	},

	// manually sets the state of the form to dirty. The change event handler
	// already takes care of this, so calling setDirty() is only necessary if
	// there are form elements outside of this.el
	setDirty: function () {
		if (this.dirty) {
			// #22757 the logic below is relatively expensive, so if the FormView
			// was already marked dirty, we don't need to run that logic again
			return
		}

		this.dirty = true

		if (!_.isBoolean(this.valid)) {
			// in case neither validate nor test have been called yet
			this.test()
		}

		this._updateButtons()
	},

	/**
	 * Sets state (styles) for the submission state, or clears them when
	 * a response is received (success or failure)
	 *
	 * While submitting, we will fade out the content and prevent user interaction
	 * until the XHR returns.
	 *
	 * @param {boolean} submitting True if a server XHR is in process
	 * @private
	 */
	_setSubmitting: function (submitting) {
		this.submitting = submitting
		this.$el.toggleClass('is-submitting-form', submitting)
	},

	// updates the state of the cancel/save buttons after a change event
	// or a manual setDirty() call
	_updateButtons: function (submitButtonEnabled) {
		// this is the default change behavior, which can easily be overwritten
		// using an event handler on form-view's 'change' event
		if (this.toggleCancel) {
			// Only enable the button if the form is dirty (#22149) or new (#22287)
			const enableCancelButton = this.dirty || this.isNew
			this.cancelButtonEl.prop('disabled', !enableCancelButton)
		}
		// make sure to record whether it's valid here, even though it's already
		// recorded in this.test in case this.test was overridden and this.valid
		// didn't get set
		this.valid = !this.test()
		// Even if the form is invalid, the submit button will be enabled so the
		// user will click it to reveal the errors. The button will be disabled
		// before a user makes any changes (unless clean submissions are allowed)
		// or after clicking it. #27703
		submitButtonEnabled = _.isNil(submitButtonEnabled) ?
			true :
			submitButtonEnabled || this.allowCleanSubmission()
		_.each(this.submitButtons, b => b.toggle(submitButtonEnabled))
	},

	/**
	 * Returns whether clean submissions are allowed
	 *
	 * Should be set to true when you want to allow clicking next
	 * even if there are no changes in the form (e.g. maybe you're creating
	 * something new and all the defaults are OK, or you're in a wizard where
	 * all the fields are optional).
	 * Can be truthy or a function that returns a truthy value

	 * @returns {Boolean}
	 */
	allowCleanSubmission: function() {
		return _.result(this.options, 'allowCleanSubmission', false)
	},

	cancel: function() {
		// disable the cancel button and the primary button
		if (this.toggleCancel) {
			// Note: disabling the button in the middle of event handling stops
			// the event from bubbling up. I considered doing a nextTick() on
			// this logic, but I didn't want to risk it in an emergency release.
			// Instead, I added a Backbone cancel event on the view.
			this.cancelButtonEl.prop('disabled', true)
		}
		// nothing should be invalid since we reset the form.  this should
		// clear everything with the least amount of code
		_.invokeMap(formHelper.getMarkedFields(this.el), 'clearError')
		_.invokeMap(this.submitButtons, 'disable')
		this.clearErrors()
		this.trigger('cancel')
	},

	_getErrorMessage: function(response) {
		if (response) {
			var responseJSON = response.responseJSON
			if (!responseJSON) {
				if (response.responseText) {
					// needed when streaming, e.g. for metric-updates
					try {
						responseJSON = JSON.parse(response.responseText)
					} catch (e) {
						// in case something REALLY wrong happened on the server
						// and we got back a non-JSON response
						return i18n.get('common.error.jsp.title')
					}
				} else {
					// this branch will be hit if the server is offline
					return escapePreserveLineBreaks(response.message)
				}
			}

			// handle multiple errors in the response, such as when the server
			// runs into one of Spring's BindExceptions
			if (responseJSON.errors) {
				return escapePreserveLineBreaks(responseJSON.errors[0].message)
			// handle simpler errors, perhaps from a MessageSourceResolvableException
			} else if (responseJSON.messageCode) {
				const args = response.responseJSON && response.responseJSON.arguments
				if (args && args.length) {
					return i18n.highlight(responseJSON.messageCode, ...args)
				}
				return escapePreserveLineBreaks(responseJSON.message)
			}
		}
	},

	// handles error responses from the server or as a string passed in
	error: function(response, options = {}) {
		if (isOffline(response)) {
			// #31389 make sure we reset the form so it doesn't look like the
			// application is hanging
			this.resetForm()
			displayErrorHandler(response, {showEmptyState: false})
			return
		}

		if (response.statusText === 'abort') return

		const handled = _.isFunction(options.handler) && options.handler(response)
		if (!handled) {
			this._defaultErrorHandler(response)
		}
	},

	_defaultErrorHandler: function (response) {
		if (_.isString(response)) {
			this.errorMessage(response)
			return
		}

		this.resetForm()

		var message = this._getErrorMessage(response)

		this.responseJSON = response.responseJSON ||
			// needed when streaming, e.g. for metric-updates
			JSON.parse(response.responseText)

		// if we have a good natural language error from the server
		if (message && response.status != HttpStatusCodes.INTERNAL_SERVER_ERROR) {
			this.errorMessage(message)
		} else if (this.responseJSON.causes) {
			//if this is a multi cause message
			let {causes, prelude, postlude} = this.responseJSON
			this.errorMessages(causes, {prelude, postlude})
		} else {
			// otherwise, show a stack trace in a dialog
			appDisplay.dialog(new ErrorView(this.responseJSON))
		}

		this.defaultBannerError()
			.toggleClass('has-stack-trace', !!this.responseJSON.stackTrace)
	},

	/**
	 * @param {FormViewStartOptions} options
	 */
	resetForm(options) {
		this.start(options) // #26239 reset the form (so it's editable) and buttons

		// #29897 enable the cancel button so that they can just cancel out of a
		// form with errors on it
		this.cancelButtonEl?.prop('disabled', false)
	},

	// renders an error inside div.banner-error
	// For this to work, the banner-error div must already be on the form in the
	// spot where the error should appear (it should appear above the form
	// controls, but if there is a non-error banner it should be under that)
	errorMessage: function(msg, stackTrace) {
		this.errorMessages([].concat(msg))
		this.stackTrace = stackTrace
		if (stackTrace) {
			this.defaultBannerError().addClass('has-stack-trace')
		}
	},

	// renders an array of errors inside div.banner-error
	// For this to work, the banner-error div must already be on the form in the
	// spot where the error should appear (it should appear above the form
	// controls, but if there is a non-error banner it should be under that)
	errorMessages: function(messages, options) {
		this._setSubmitting(false)
		options = options || {}

		this.ensureBannerError()
		let bannerError = this.defaultBannerError()

		bannerError.toggleClass('has-multiple-errors', messages.length > 1)
		this.errorHtml(this.multiCauseMessage(messages, options))

		// scroll to the top so we can see the error message
		this.$('.scroll-container, .dialog-body')
			.scrollTop(0)
			.scrollLeft(0)

		// reposition any marked-field tooltips
		_.each(this.$('.marked-field'), el => {
			this.$(el).data('view').repositionTooltip()
		})
	},

	/**
	 * Formats a message which includes a prelude, causes, and postlude and returns the message.
	 * The causes are rendered as an unordered list.
	 * @param {object} options
	 * @param {string} options.prelude
	 * @param {boolean} options.escapePrelude
	 * @param {string} options.postlude
	 * @param {boolean} options.escapePostlude
	 */
	multiCauseMessage: function(causes, options) {
		let errorContents = ''
		// If it has a prelude it is a MessageWithPreludeException
		if ((options.prelude != null && causes.length > 0) ||
			causes.length > 1) {
			if (options.prelude) {
				if (options.escapePrelude) {
					options.prelude = _.escape(options.prelude)
				}
				errorContents += '<p>' + options.prelude + '</p>'
			}
			errorContents += '<ul>'
			_.forEach(causes, function (msg) {
				errorContents += '<li>' + _.escape(msg) + '</li>'
			}.bind(this))
			errorContents += '</ul>'
			if (options.postlude) {
				if (options.escapePostlude) {
					options.postlude = _.escape(options.postlude)
				}
				errorContents += '<p>' + options.postlude + '</p>'
			}
		} else {
			errorContents += _.first(causes)
		}
		return errorContents
	},

	// internal implementation method.  call errorHtml instead.
	_errorHtml: function(html) {
		this.$el.addClass('has-error')
		_.each(this.submitButtons, b => b.disable())

		let bannerError = this.defaultBannerError()
		bannerError.parent().removeClass('is-not-displayed')
		bannerError.first()
			.removeClass('is-not-displayed')
			.html(html)
	},

	// renders an error inside div.banner-error
	// For this to work, the banner-error div must already be on the form in the
	// spot where the error should appear (it should appear above the form
	// controls, but if there is a non-error banner it should be under that)
	errorHtml: function(html) {
		this._setSubmitting(false)
		this._errorHtml(html)
		this.defaultBannerError().first()
			.toggleClass('has-multiple-errors', false)
		this.trigger('error')
	},

	/**
	 * Ensures there's a .banner-error element in the form.  Currently only
	 * works for dialogs.
	 */
	ensureBannerError: function() {
		if (this.$('.banner-error').length == 0) {
			var bannerDiv = '<div class="banner banner-error"></div>'
			// if there's a banner, the banner-error should go underneath it
			if (this.$('.banner').length != 0) {
				this.$('.banner').first().after(bannerDiv)
			} else {
				this.$('.dialog-body-content').prepend(bannerDiv)
			}
		}
	},

	onClickBannerError: function(e) {
		if (conductor.isNavigationTarget(e)) {
			return
		}
		let errorViewOptions
		if (this.responseJSON && this.responseJSON.stackTrace) {
			this.responseJSON.bypassMessageView = true
			errorViewOptions = this.responseJSON
		} else if (this.stackTrace) {
			errorViewOptions = { stackTrace: this.stackTrace }
		}

		if (errorViewOptions) {
			appDisplay.dialog(new ErrorView(errorViewOptions))
		}
	},

	// Clears the messages displayed in the error banner and any marked-field
	// tooltips
	clearErrors: function () {
		let markedFields = formHelper.getMarkedFields(this.el, false)
		_.invokeMap(markedFields, 'clearError')
		this.$el.removeClass('has-error')
		this.$('.banner-error').empty().addClass('is-not-displayed')
		this.$('.banner-container').addClass('is-not-displayed')
		this.trigger('cleared-errors')
	},

	/**
	 * Tests the form for errors. By default, tests all the marked-fields for
	 * errors, and returns the views with errors. Additionally, will return
	 * any errors from the options.test function
	 *
	 * @returns {View[]|String[]}
	 */
	test: function() {
		var result = formHelper.test(this.$el) || []

		let customErrors = _.invoke(this.options, 'test')
		if (customErrors) {
			if (_.isArray(customErrors)) {
				result = result.concat(customErrors)
			} else {
				result.push(customErrors)
			}
		}

		this.valid = !result
		return _.isEmpty(result) ? null : result
	},

	// validate the form, display any relevant error messages and return an
	// array of errors found.  if there are no errors, returns null
	validate: function() {
		this.clearErrors()
		var result = formHelper.validate(this.$el)
		if (!result) {
			// if the test method isn't overwritten this isn't necessary, but
			// if it is overwritten this allows us to avoid having to override
			// this.validate as well
			result = this.test()
			if (!_.isNil(result)) {
				if ((!_.isString(result) && !_.isArray(result)) || result === '') {
					throw Error('A valid error message needs to be returned')
				}
				this.errorMessage(result)
			}
		}

		this.valid = !result
		return result
	},

	defaultBannerError () {
		// Look for the first banner-error with a visible parent
		let bannerError = this.$('.banner-error')
			.parent()
			.filter(function () { return $(this).is(':visible') })
			.find('.banner-error')
			.first()
		if (bannerError.length == 0) {
			// If we didn't find one with a visible parent, just use the first
			// one we find
			bannerError = this.$('.banner-error').first()
		}
		return bannerError
	},

	/**
	 * Callback for when a form is submitted using the secondary submit button.
	 * Stops event propagation, removes any error indicators, performs validation,
	 * and toggles the state of the submit buttons.
	 * If validation succeeds, executes this.options.submitSecondary.
	 */
	submitSecondary: function(e) {
		if (this._doSubmit(e, this.secondaryButton, this.primaryButton)) {
			_.invoke(this.options, 'submitSecondary', e)
		}
	},

	/**
	 * Callback for when a form is submitted using the primary submit button.
	 * Stops event propagation, removes any error indicators, performs validation,
	 * and toggles the state of the submit buttons.
	 * If validation succeeds, executes this.options.submit.
	 */
	submit: function(e) {
		if (this._doSubmit(e, this.primaryButton, this.secondaryButton)) {
			this.trigger('submit')
			let xhr = _.invoke(this.options, 'submit', e)
			if (!xhr) {
				console.warn('no xhr returned from formView.submit. This may cause problems like #41321')
			}
			return xhr
		}
	},

	/**
	 * @param {Event} e The source event that triggered the submit
	 *
	 * @param {BusyButton} clickedButton The submit button the user submitted the
	 * form with.
	 *
	 * @param {BusyButton} [otherButton] If the form contains a secondary button
	 * this is the button that they did not interact with.
	 *
	 * @returns {Boolean} true if the form can be submitted
	 */
	_doSubmit: function(e, clickedButton, otherButton) {
		if (this.options.preventButtonSetup) return

		this._terminateEvent(e)

		// https://github.com/SpiderStrategies/Scoreboard/issues/36407#issuecomment-808428945
		if (this.submitting) {
			return false
		}

		// #23688 don't allow forms to be submitted multiple times
		if (clickedButton.$el.hasClass('is-loading') ||
			clickedButton.$el.hasClass('is-done')) {
			return false
		}
		if (!this.started) {
			throw new Error('Never called FormView.start')
		}

		// first remove any error messages, since we're going to run validation
		// again right now
		this.clearErrors()

		// if validation fails or the form isn't dirty, the form can't be
		// submitted.  If you're creating something new, you'll get tooltips
		// showing you what's wrong.  If you're editing something, the button
		// will simply appear to not work
		let valid = _.isEmpty(this.validate())
		if (!valid || (!this.allowCleanSubmission() && !this.dirty)) {
			_.each(this.submitButtons, b => b.disable())
		} else if (this.allowCleanSubmission() && !valid
				&& clickedButton.state == 'disabled') {
			// if a form view is allowing clean submission, there are no validation errors,
			// but the primary button was previously set to disabled for another reason
			// do not attempt to submit the form (see issue #9572)
		} else {
			this._setSubmitting(true)
			this.guard && guard.removeGuard(this.guard)
			// note: canceling should be allowed even if the form even if the
			// form is already submitted
			clickedButton.loading()
			_.invoke(otherButton, 'disable')
			return true
		}
	},

	/**
	 * If a listener function was called with an Event this function prevents
	 * it from propagating up and being handled by other FormViews.
	 *
	 * Callers of this form need to listen to the events triggered by this view
	 * e.g. ('submit', 'change', 'cancel', etc) instead of relying on event
	 * propagation.
	 *
	 * @param {Event|Object} [e]
	 */
	_terminateEvent: function(e) {
		// the argument SHOULD be an event, but in some cases changes have been
		// found to be triggered with other values.
		_.invoke(e, 'preventDefault')
		_.invoke(e, 'stopPropagation')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.guard && guard.removeGuard(this.guard)
	}

})

export default FormView

FormView.__filename__ = 'components/form-view.js'
